import { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import DateInput from "../DateInput";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Plus } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const METODI_USCITA = ["Contante", "POS", "Bonifico", "Carta di Credito"];

export const UscitaDialog = ({ open, onOpenChange, form, onFormChange, tipologieUscita, sottotipologieUscita = [], fornitori, getAuthHeaders, onFornitoreCreated, onSubmit }) => {
  const [newFornitoreDialog, setNewFornitoreDialog] = useState(false);
  const [newFornitoreName, setNewFornitoreName] = useState("");

  const handleCreateFornitore = async (e) => {
    e.preventDefault();
    if (!newFornitoreName.trim()) return;
    try {
      const headers = getAuthHeaders();
      const res = await axios.post(`${API}/fornitori`, { ragione_sociale: newFornitoreName.trim() }, { headers });
      toast.success("Fornitore creato");
      onFornitoreCreated(res.data);
      onFormChange({ ...form, fornitore_id: res.data.id });
      setNewFornitoreDialog(false);
      setNewFornitoreName("");
    } catch {
      toast.error("Errore nella creazione");
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="bg-card border-border">
          <DialogHeader><DialogTitle className="text-foreground">Nuova Uscita</DialogTitle></DialogHeader>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-foreground/70">Data</Label>
              <DateInput value={form.data || ""} onChange={(v) => onFormChange({ ...form, data: v })} />
            </div>
            <div className="space-y-2">
              <Label className="text-foreground/70">Costo (€) *</Label>
              <Input type="number" step="0.01" value={form.importo || ""} onChange={(e) => onFormChange({ ...form, importo: e.target.value })} placeholder="0.00" required className="bg-card border-border text-foreground" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-foreground/70">Come *</Label>
                <Select value={form.metodo || ""} onValueChange={(v) => onFormChange({ ...form, metodo: v })}>
                  <SelectTrigger className="bg-card border-border text-foreground"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                  <SelectContent>
                    {METODI_USCITA.map((m) => (<SelectItem key={m} value={m}>{m}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-foreground/70">Tipologia *</Label>
                <Select value={form.tipologia_id || ""} onValueChange={(v) => onFormChange({ ...form, tipologia_id: v, sottotipologia_id: null })}>
                  <SelectTrigger className="bg-card border-border text-foreground" data-testid="new-expense-type"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                  <SelectContent>
                    {tipologieUscita.map((t) => (<SelectItem key={t.id} value={t.id}>{t.nome}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {form.tipologia_id && <div className="space-y-2"><Label className="text-foreground/70">Sotto-tipologia (facoltativa)</Label>
              <Select value={form.sottotipologia_id || "none"} onValueChange={v => onFormChange({ ...form, sottotipologia_id: v === "none" ? null : v })}>
                <SelectTrigger data-testid="new-expense-subtype" className="bg-card border-border text-foreground"><SelectValue placeholder="Nessuna" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Nessuna</SelectItem>{sottotipologieUscita.filter(s => s.tipologia_id === form.tipologia_id).map(s => <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>)}</SelectContent>
              </Select>
            </div>}
            <div className="space-y-2">
              <Label className="text-foreground/70">Fornitore</Label>
              <Select value={form.fornitore_id || "none"} onValueChange={(v) => {
                if (v === "__new__") {
                  setNewFornitoreDialog(true);
                } else {
                  onFormChange({ ...form, fornitore_id: v === "none" ? "" : v });
                }
              }}>
                <SelectTrigger className="bg-card border-border text-foreground"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">- Nessuno -</SelectItem>
                  {[...fornitori].sort((a, b) => a.ragione_sociale.localeCompare(b.ragione_sociale)).map((f) => (<SelectItem key={f.id} value={f.id}>{f.ragione_sociale}</SelectItem>))}
                  <SelectItem value="__new__" className="text-lime-400"><Plus className="h-3 w-3 inline mr-1" />Aggiungi</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-foreground/70">Descrizione</Label>
              <Textarea value={form.descrizione || ""} onChange={(e) => onFormChange({ ...form, descrizione: e.target.value })} placeholder="Descrizione" className="bg-card border-border text-foreground" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="border-border text-foreground hover:bg-accent hover:text-accent-foreground">Annulla</Button>
              <Button type="submit" className="bg-red-500 hover:bg-red-400 text-white">Salva</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={newFornitoreDialog} onOpenChange={setNewFornitoreDialog}>
        <DialogContent className="max-w-sm bg-card border-border">
          <DialogHeader><DialogTitle className="text-foreground">Nuovo Fornitore</DialogTitle></DialogHeader>
          <form onSubmit={handleCreateFornitore} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-foreground/70">Ragione Sociale *</Label>
              <Input value={newFornitoreName} onChange={(e) => setNewFornitoreName(e.target.value)} placeholder="Nome fornitore" autoFocus className="bg-card border-border text-foreground" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setNewFornitoreDialog(false)} className="border-border text-foreground hover:bg-accent hover:text-accent-foreground">Annulla</Button>
              <Button type="submit" className="bg-lime-500 hover:bg-lime-400 text-black">Crea</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};
